import React from "react"
import { Dialog as MuiDialog, DialogContent, Box, makeStyles, Fade } from "@material-ui/core"
import clsx from "clsx"
import { DialogActions, DialogTitle } from ".."
import { ErrorBoundary } from "../ErrorBoundary"

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    marginTop: ({ isInline }) => (isInline ? theme.dimensions.header.height : 0),
    marginBottom: ({ isInline }) => (isInline ? theme.dimensions.footer.mobileBottomNav.height : 0),
    "& .MuiDialog-paperFullScreen": {
      marginTop: ({ disableHeader }) => (disableHeader ? 0 : theme.dimensions.header.height),
      marginBottom: ({ disableFooter }) => (disableFooter ? 0 : theme.dimensions.footer.mobileBottomNav.height),
      height: ({ disableFooter, disableHeader }) =>
        `calc(100% - ${disableHeader ? 0 : theme.dimensions.header.height}px - ${disableFooter ? 0 : theme.dimensions.footer.mobileBottomNav.height}px)`
    },
    "& .MuiDialogContent-root:first-child": {
      paddingTop: 0
    },
    "& .MuiDialog-paper": {
      height: ({ fullHeight, isInline }) => (!isInline && fullHeight ? "100%" : "")
    }
  },
  title: {
    background: ({ stickyTitle }) => (stickyTitle ? theme.palette.background.default : "transparent"),
    position: ({ stickyTitle }) => (stickyTitle ? "sticky" : "static"),
    borderBottom: `1px solid ${theme.palette.grey[200]}`,
    top: 0
  },
  content: {
    padding: ({ isInline, disablePadding }) =>
      isInline ? (disablePadding ? 0 : theme.spacing(2)) : disablePadding ? 0 : theme.spacing(2, 3, 2, 3),
    flexGrow: ({ stickyActions }) => (stickyActions ? 1 : 0)
  },
  actions: {
    background: ({ stickyActions }) => (stickyActions ? theme.palette.background.default : "transparent"),
    position: ({ stickyActions }) => (stickyActions ? "sticky" : "static"),
    borderTop: ({ stickyActions }) => (stickyActions ? `1px solid ${theme.palette.grey[200]}` : "none"),
    bottom: 0
  }
}))

const Dialog = ({
  id,
  open,
  onClose,
  title,
  children,
  actions,
  isInline = false,
  stickyTitle = false,
  stickyActions = true,
  disableHeader = true,
  disableFooter = true,
  maxWidth = "md",
  disablePadding = false,
  disableEnforceFocus = false,
  fullHeight = false,
  boxProps,
  customActionsContainerClass = "",
  ...rest
}) => {
  const classes = useStyles({
    disablePadding,
    disableFooter,
    disableHeader,
    isInline,
    stickyTitle,
    stickyActions,
    fullHeight
  })
  const hasActions = actions && actions?.props?.children !== false

  const dialogActions = actions ? (
    <DialogActions
      isInline={isInline}
      stickyActions={stickyActions}
      id="form-dialog-actions"
      className={clsx(classes.actions, customActionsContainerClass)}
      {...rest}
    >
      {actions}
    </DialogActions>
  ) : null

  return (
    <MuiDialog
      fullScreen={isInline}
      hideBackdrop={isInline}
      fullWidth
      open={open}
      onClose={onClose}
      aria-labelledby={title ? "form-dialog-title" : "form-dialog"}
      maxWidth={maxWidth}
      disableEnforceFocus={disableEnforceFocus}
      className={classes.root}
      TransitionComponent={Fade}
      transitionDuration={300}
    >
      {title && (
        <DialogTitle className={classes.title} isInline={isInline} id="form-dialog-title" onClose={onClose}>
          {title}
        </DialogTitle>
      )}

      <ErrorBoundary>
        {isInline ? (
          // Variation 1: Inline display as used on small devices
          <>
            {/* Displays the actions directly under the form  */}
            <Box className={classes.content}>
              {children}
              {hasActions && !stickyActions && dialogActions}
            </Box>

            {/* Displays sticky actions at bottom of the dialog  */}
            {hasActions && stickyActions && dialogActions}
          </>
        ) : (
          // Variation 2: Regular dialog display
          <>
            {/* Displays the actions under the form  */}
            <DialogContent id={id} className={classes.content}>
              {children}
              {hasActions && !stickyActions && dialogActions}
            </DialogContent>

            {/* Displays sticky actions at bottom of the dialog  */}
            {hasActions && stickyActions && dialogActions}
          </>
        )}
      </ErrorBoundary>
    </MuiDialog>
  )
}

export { Dialog }
